import { useState } from "react";

import {
  formatInvoiceNumber,
  Invoice,
  INVOICE_STATUS_APPROVED_BY_SET,
  INVOICE_STATUS_CANCELLATION_FAILED,
  INVOICE_STATUS_CANCELLATION_REQUESTED,
  INVOICE_STATUS_CANCELLED,
  INVOICE_STATUS_DRAFT,
  INVOICE_STATUS_DRAFT_DISCARDED,
  INVOICE_STATUS_ERROR,
  INVOICE_STATUS_NOMINATED,
  INVOICE_STATUS_RESEND_REQUESTED,
  INVOICE_STATUS_SET_UNKNOWN_ERROR,
  INVOICE_STATUS_UPLOADED_TO_SET,
} from "../../models/invoice";
import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCollapse,
  CTooltip,
} from "@coreui/react";
import { formatUTCDateTime } from "../../utils/dates";
import { formatToCurrency } from "../../currency/format";
import { CURRENCY_CHOICES } from "../../currency/available-currencies";
import InvoiceCrudStatus from "./InvoiceCrudStatus";
import InvoiceCrudActions from "./InvoiceCrudActions";
import DocumentPrintButton from "../documents/DocumentPrintButton";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";
import { INVOICES_RETRIEVE } from "../../auth/permissions";

interface InvoiceCardProps {
  invoice: Invoice;
  onXMLClick: (inv: Invoice) => void;
  onPDFClick: (inv: Invoice) => void;
  onCancellationClick: (inv: Invoice) => void;
  onCancellationRetryClick: (inv: Invoice) => void;
  onCancellationRevertClick: (inv: Invoice) => void;
  onReadClick: (inv: Invoice) => void;
  onUpdateClick: (inv: Invoice) => void;
  onDeleteClick: (inv: Invoice) => void;
  onConsultByCdcClick: (inv: Invoice) => void;
  onResendClick: (inv: Invoice) => void;
  onSendEmailClick: (inv: Invoice) => void;
  onNominateClick: (inv: Invoice) => void;
  onHistoryClick: (inv: Invoice) => void;
  onConfirmDraftClick: (inv: Invoice) => void;
  onDiscardDraftClick: (inv: Invoice) => void;
  onRegenerateKudeClick: (inv: Invoice) => void;
}

export function InvoiceCard({
  invoice,
  onXMLClick,
  onPDFClick,
  onCancellationClick,
  onCancellationRetryClick,
  onCancellationRevertClick,
  onReadClick,
  onUpdateClick,
  onDeleteClick,
  onConsultByCdcClick,
  onResendClick,
  onSendEmailClick,
  onNominateClick,
  onHistoryClick,
  onConfirmDraftClick,
  onDiscardDraftClick,
  onRegenerateKudeClick,
}: InvoiceCardProps) {
  const [open, setOpen] = useState(true);

  const getStatusColor = (status?: string): string => {
    switch (status) {
      case INVOICE_STATUS_UPLOADED_TO_SET:
        return "warning";
      case INVOICE_STATUS_ERROR:
        return "danger";
      case INVOICE_STATUS_SET_UNKNOWN_ERROR:
        return "danger";
      case INVOICE_STATUS_CANCELLATION_FAILED:
        return "danger";
      case INVOICE_STATUS_APPROVED_BY_SET:
        return "success";
      case INVOICE_STATUS_NOMINATED:
        return "success";
      case INVOICE_STATUS_CANCELLATION_REQUESTED:
        return "light";
      case INVOICE_STATUS_CANCELLED:
        return "info";
      case INVOICE_STATUS_RESEND_REQUESTED:
        return "warning";
      case INVOICE_STATUS_DRAFT:
        return "secondary";
      case INVOICE_STATUS_DRAFT_DISCARDED:
        return "dark";
      default:
        return "info";
    }
  };

  let xmlButton = null;
  let pdfButton = null;

  if (invoice.xmlPath !== undefined) {
    xmlButton = (
      <PermissionRequiredComponent permissionName={INVOICES_RETRIEVE}>
        <CTooltip content="XML">
          <CButton
            className="text-white"
            color="info"
            onClick={() => {
              onXMLClick(invoice);
            }}
          >
            <i className="fa fa-code"></i>
          </CButton>
        </CTooltip>
      </PermissionRequiredComponent>
    );
  }

  if (invoice.pdfPath !== undefined) {
    pdfButton = (
      <PermissionRequiredComponent permissionName={INVOICES_RETRIEVE}>
        <CTooltip content="PDF">
          <CButton
            className="text-white"
            color="danger"
            onClick={() => {
              onPDFClick(invoice);
            }}
          >
            <i className="fa fa-file-pdf"></i>
          </CButton>
        </CTooltip>
      </PermissionRequiredComponent>
    );
  }

  return (
    <CCard className="mb-2">
      <CCardHeader
        className="d-flex justify-content-between align-items-center cursor-pointer"
        onClick={() => setOpen(!open)}
        color={getStatusColor(invoice.status)}
      >
        <span>
          <strong>{formatInvoiceNumber(invoice)}</strong>
          {invoice.exportData && <small className="d-block">Exportación</small>}
        </span>
        <span className="d-flex align-items-center gap-2">
          <strong>{formatUTCDateTime(new Date(invoice.invoiceDate!))}</strong>
          <span
            className="rounded-circle bg-white d-flex justify-content-center align-items-center ml-1"
            style={{ width: "22px", height: "22px" }}
          >
            <i
              className={`fa fa-chevron-${open ? "up" : "down"}`}
              style={{ color: "black", fontSize: "12px" }}
            />
          </span>
        </span>
      </CCardHeader>
      <CCollapse show={open}>
        <CCardBody>
          <p>
            <strong>Razón Social:</strong> {invoice.clientSocialName}
          </p>
          <p>
            <strong>RUC:</strong> {invoice.clientRuc}
          </p>
          <p>
            <strong>Monto:</strong>{" "}
            {formatToCurrency(invoice.amount!, invoice.currency!)}
          </p>
          {invoice.totalDiscount && (
            <p>
              <strong>Descuento:</strong>{" "}
              {formatToCurrency(invoice.totalDiscount, invoice.currency!)}
            </p>
          )}
          <p>
            <strong>Moneda:</strong> {CURRENCY_CHOICES.get(invoice.currency!)}
          </p>
          <p>
            <strong>Timbrado Usado:</strong> {invoice.stampIdentifier}
          </p>

          <p>
            <strong>Estado:</strong> <InvoiceCrudStatus invoice={invoice} />
          </p>

          <div>
            <strong>Archivos:</strong>{" "}
            <td className="text-center">
              <CButtonGroup>
                {xmlButton}
                {pdfButton}
                {invoice.pdfPath && invoice.id !== undefined && <DocumentPrintButton kind="invoice" documentId={invoice.id} />}
              </CButtonGroup>
            </td>
          </div>
          <div>
            <strong>Acciones disponibles:</strong>{" "}
            <InvoiceCrudActions
              invoice={invoice}
              onCancellationClick={onCancellationClick}
              onCancellationRetryClick={onCancellationRetryClick}
              onCancellationRevertClick={onCancellationRevertClick}
              onReadClick={onReadClick}
              onUpdateClick={onUpdateClick}
              onDeleteClick={onDeleteClick}
              onConsultByCdcClick={onConsultByCdcClick}
              onResendClick={onResendClick}
              onSendEmailClick={onSendEmailClick}
              onNominateClick={onNominateClick}
              onHistoryClick={onHistoryClick}
              onConfirmDraftClick={onConfirmDraftClick}
              onDiscardDraftClick={onDiscardDraftClick}
              onRegenerateKudeClick={onRegenerateKudeClick}
            />
          </div>
        </CCardBody>
      </CCollapse>
    </CCard>
  );
}
